import { useThrottleFn } from 'ahooks'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

import type { FnChild } from './DropDown'

interface IUseEvents {
  inPopupOrChild: (element: Node) => boolean
  popupEle: HTMLDivElement | null
  open: boolean
  triggerOpen: (nextOpen: boolean) => void
  isTooltipOrHoverTrigger?: boolean
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  refRoot: React.RefObject<HTMLDivElement>
  children?: ReactNode | FnChild
  handlers: {
    onClickOverlay: (event: MouseEvent | React.MouseEvent) => void
    onHoverOverlay: () => void
    handleFocus: (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => void
    handleBlur: (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => void
  }
}

const THROTL_DELAY = 100

export const useEvents = ({
  inPopupOrChild,
  triggerOpen,
  open,
  popupEle,
  isTooltipOrHoverTrigger,
  timeoutRef,
  refRoot,
  children,
  handlers,
}: IUseEvents) => {
  const { run: onOutsideLeave } = useThrottleFn(
    (e: MouseEvent) => {
      const target = e.target as Node
      if (isTooltipOrHoverTrigger && !inPopupOrChild(target)) {
        if (open) {
          triggerOpen(false)
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      }
    },
    { wait: THROTL_DELAY },
  )

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    const onWindowClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (open && !inPopupOrChild(target)) {
        triggerOpen(false)
      }
    }

    const closeOnEsc = (e: KeyboardEvent) => {
      const target = e.target as Node
      if (e.key === 'Escape' && open && !inPopupOrChild(target)) {
        triggerOpen(false)
      }
    }

    document.addEventListener('mousedown', onWindowClick, { signal, capture: true })
    document.addEventListener('keydown', closeOnEsc, { signal })
    document.addEventListener('mouseover', onOutsideLeave, { signal, capture: true })

    return () => {
      abortController.abort()
    }
  }, [popupEle, open, inPopupOrChild, onOutsideLeave, triggerOpen])

  useEffect(() => {
    const rootCurrentRef = refRoot.current
    if (!rootCurrentRef || typeof children === 'function') {
      return
    }
    const { handleBlur, handleFocus, onClickOverlay, onHoverOverlay } = handlers

    const abortController = new AbortController()
    const signal = abortController.signal

    rootCurrentRef.addEventListener('click', onClickOverlay, { signal })
    rootCurrentRef.addEventListener('mouseenter', onHoverOverlay, { signal })
    rootCurrentRef.addEventListener('blur', handleBlur, { signal, capture: true })
    rootCurrentRef.addEventListener('focus', handleFocus, { signal, capture: true })

    return () => {
      abortController.abort()
    }
  }, [refRoot, children, handlers, open])
}
